import {
  GetPaginatedAssetsForSearchAndSelectQuery,
  GetPortfolioAssetsForAssetSelectQuery,
  AssetsListGroup,
} from '@bts-web/data-layer/server';
import { PrecisionFloat } from '@bts-web/utils-formatting';

type TOwnedAssetType = {
  type: 'owned';
  ownedAmountFiat: PrecisionFloat;
  ownedAmountAsset: PrecisionFloat;
};

type TAllAssetType = {
  type: 'all';
  value: PrecisionFloat;
};

export type AssetSelectDrawerItemEntry = {
  name: string;
  id: string;
  logoUrl: string | null | undefined;
  symbol: string | null | undefined;
  disabled?: boolean;
} & (TOwnedAssetType | TAllAssetType);

export interface AssetSelectHookResult {
  assets: AssetSelectDrawerItemEntry[];
  assetFilterSearchValue: string;
  onInputChange: (newValue: string) => void;
  clearSearchParams: () => void;
  isLoading: boolean;
  canLoadMore?: boolean;
  loadMore?: () => Promise<void>;
  groups?: AssetsListGroup[] | undefined;
  setGroups?: (groups: AssetsListGroup[] | undefined) => void;
  assetGroups: GetPaginatedAssetsForSearchAndSelectQuery['response']['assetGroups'];
}

export const getPaginatedAssetsForSelectInitialQueryParams = {
  first: 10,
} satisfies GetPaginatedAssetsForSearchAndSelectQuery['variables']['input'];

export const extractAssetsFromGeneralFetchAllResponse = ({
  responseData,
}: {
  responseData: GetPaginatedAssetsForSearchAndSelectQuery['response'];
}): AssetSelectDrawerItemEntry[] => {
  return (
    responseData?.assets?.edges?.reduce<AssetSelectDrawerItemEntry[]>(
      (acc, item) => {
        if (!item?.node) return acc;

        acc.push({
          id: item.node.id,
          name: item.node.name ?? '',
          logoUrl: item.node.logoUrl ?? null,
          symbol: item.node.symbol ?? null,
          type: 'all',
          value: item.node.price,
        });

        return acc;
      },
      [],
    ) ?? []
  );
};

export const extractAssetsFromPortfolioFetchAllResponse = ({
  responseData,
}: {
  responseData: GetPortfolioAssetsForAssetSelectQuery['response'];
}): AssetSelectDrawerItemEntry[] => {
  const result: AssetSelectDrawerItemEntry[] = [];

  if (!responseData?.portfolio?.assetGroups?.edges) {
    return result;
  }

  responseData.portfolio.assetGroups.edges.forEach((assetGroup) => {
    assetGroup?.node?.forEach((nodeGroup) => {
      nodeGroup?.edges?.forEach((edge) => {
        const node = edge?.node;

        if (!node) return;

        result.push({
          type: 'owned',
          name: node.name ?? '',
          id: node.id,
          logoUrl: node.logoUrl ?? null,
          symbol: node.symbol ?? null,
          ownedAmountFiat: edge.fiatBalance,
          ownedAmountAsset: edge.assetBalance,
        });
      });
    });
  });

  return result;
};
